import { memo, useEffect } from 'react';
import { useSelector } from 'react-redux';
// Components
import Advanced from 'components/Advanced';
import Comments from 'components/Comments';
import { get } from 'lodash';
import { advancedFieldsSelector, isNewFields } from 'slices/field.slice';
import { EFieldType, EPreferencesFilter } from '@timeedit/types/lib/enums';
import type { TField } from '@timeedit/types/lib/types';
import { TEDrawerSection } from '@timeedit/ui-components';
import Attributes from './sections/Attributes';
import { GeneralInfo } from './sections/GeneralInfo';
import { Language } from './sections/Language';
import { Properties } from './sections/Properties';

type TFieldDetail = {
  value: TField;
  onChange: (data: { key: string; value: any; obj?: any }) => void;
};
const FieldDetail = ({ value, onChange }: TFieldDetail) => {
  const advancedSettings: any = useSelector(advancedFieldsSelector(value.id));
  const onChangeValue = (key: string, value: any, obj?: any) => {
    onChange({ key, value, obj });
  };

  useEffect(() => {
    if (isNewFields(value)) {
      const defaultValue = get(MapDefaultValue, [value.fieldType], {});
      onChange({ key: '', value: '', obj: defaultValue });
    }
  }, [value.fieldType]);

  return (
    <>
      <GeneralInfo value={value} onChangeValue={onChangeValue} />
      <Properties value={value} onChangeValue={onChangeValue} />
      <Attributes value={value} onChangeValue={onChangeValue} />
      <TEDrawerSection label="Advanced">
        {isNewFields(value) ? null : (
          <Advanced
            options={advancedSettings || []}
            selectedUrl={'fields'}
            selectedId={value?.id}
          />
        )}
      </TEDrawerSection>
      <Language
        value={value}
        onChangeValue={(newAltDesignations) =>
          onChangeValue('altDesignations', newAltDesignations)
        }
      />
      <Comments
        value={value.documentation}
        onChange={(e) => onChangeValue('documentation', e)}
        data-testid="comment"
      />
    </>
  );
};

export default memo(FieldDetail);

const MapDefaultValue = {
  [EFieldType.TEXT]: {
    filter: '',
    length: 192,
    min: 0,
    max: 0,
    sumType: undefined,
    titleType: undefined,
    topLevelType: undefined,
    refSeparator: undefined,
    searchableInReservationList: false,
    reservationTextField: false,
    mandatory: false,
    unique: false,
    multiple: false,
    editable: true,
    searchable: true,
    listable: true,
    primary: false,
    sortable: false,
    virtualCategory: undefined,
    abstractCategory: undefined,
  },
  [EFieldType.SIGNATURE]: {
    filter: '',
    min: 0,
    refSeparator: undefined,
    searchableInReservationList: false,
    reservationTextField: false,
    mandatory: false,
    unique: false,
    multiple: false,
    primary: false,
    sortable: false,
    virtualCategory: undefined,
    abstractCategory: undefined,

    sumType: undefined,
    topLevelType: undefined,
    titleType: undefined,
    length: 10,
    max: 0,
    editable: true,
    searchable: true,
    listable: true,
  },
  [EFieldType.EMAIL]: {
    min: 0,
    refSeparator: undefined,
    searchableInReservationList: false,
    reservationTextField: false,
    mandatory: false,
    unique: false,
    multiple: false,
    primary: false,
    sortable: false,
    virtualCategory: undefined,
    abstractCategory: undefined,

    sumType: undefined,
    topLevelType: undefined,
    titleType: undefined,
    length: 50,
    max: 0,
    editable: true,
    searchable: false,
    listable: true,
    filter: EPreferencesFilter.CHOICE5,
  },
  [EFieldType.URL]: {
    filter: '',
    min: 0,
    max: 0,
    topLevelType: undefined,
    refSeparator: undefined,
    searchableInReservationList: false,
    reservationTextField: false,
    mandatory: false,
    unique: false,
    multiple: false,
    primary: false,
    sortable: false,
    virtualCategory: undefined,
    abstractCategory: undefined,

    sumType: undefined,
    titleType: undefined,
    length: 100,
    editable: true,
    listable: true,
    searchable: false,
  },
  [EFieldType.TELEPHONE]: {
    filter: EPreferencesFilter.CHOICE2,
    min: 0,
    max: 0,
    topLevelType: undefined,
    refSeparator: undefined,
    searchableInReservationList: false,
    reservationTextField: false,
    mandatory: false,
    multiple: false,
    primary: false,
    sortable: false,
    virtualCategory: undefined,
    abstractCategory: undefined,

    sumType: undefined,
    titleType: undefined,
    length: 15,
    editable: true,
    searchable: true,
    listable: true,
    unique: false,
  },
  [EFieldType.CATEGORY]: {
    sumType: undefined,
    topLevelType: undefined,
    titleType: undefined,
    length: 48,
    max: 0,
    editable: true,
    searchable: true,
    listable: true,
    filter: '',

    min: 0,
    refSeparator: undefined,
    searchableInReservationList: false,
    reservationTextField: false,
    mandatory: false,
    unique: false,
    multiple: false,
    primary: false,
    sortable: false,
    virtualCategory: undefined,
    abstractCategory: undefined,
  },
  [EFieldType.COMMENT]: {
    sumType: undefined,
    topLevelType: undefined,
    titleType: undefined,
    length: 255,
    max: 0,
    editable: true,
    multiple: false,
    searchable: false,
    filter: '',

    min: 0,
    refSeparator: undefined,
    searchableInReservationList: false,
    reservationTextField: false,
    mandatory: false,
    unique: false,
    listable: false,
    primary: false,
    sortable: false,
    virtualCategory: undefined,
    abstractCategory: undefined,
  },
  [EFieldType.INTEGER]: {
    sumType: undefined,
    topLevelType: undefined,
    titleType: undefined,
    editable: true,
    min: 0,
    max: 100,
    length: undefined,
    searchable: true,
    listable: true,
    filter: '',
    //
    refSeparator: undefined,
    searchableInReservationList: false,
    reservationTextField: false,
    mandatory: false,
    unique: false,
    multiple: false,
    primary: false,
    sortable: false,
    virtualCategory: undefined,
    abstractCategory: undefined,
  },
  [EFieldType.LENGTH]: {
    filter: '',
    length: undefined,
    min: 0,
    max: 24,
    sumType: undefined,
    titleType: undefined,
    topLevelType: undefined,
    refSeparator: undefined,
    searchableInReservationList: false,
    reservationTextField: false,
    mandatory: false,
    unique: false,
    multiple: false,
    editable: true,
    searchable: false,
    listable: true,
    primary: false,
    sortable: false,
    virtualCategory: undefined,
    abstractCategory: undefined,
  },
  [EFieldType.CHECKBOX]: {
    sumType: undefined,
    topLevelType: undefined,
    titleType: undefined,
    length: 0,
    max: 0,
    editable: true,
    searchable: true,
    listable: true,
    filter: '',
    refSeparator: undefined,
    searchableInReservationList: false,
    reservationTextField: false,
    mandatory: false,
    unique: false,
    multiple: false,
    primary: false,
    sortable: false,
    virtualCategory: undefined,
    abstractCategory: undefined,
  },
  [EFieldType.REFERENCE]: {
    sumType: undefined,
    topLevelType: undefined,
    titleType: undefined,
    max: 0,
    length: undefined,
    searchable: true,
    editable: false,
    listable: true,
    filter: '',
    //
    refSeparator: undefined,
    searchableInReservationList: false,
    reservationTextField: false,
    mandatory: false,
    unique: false,
    multiple: false,
    primary: false,
    sortable: false,
    virtualCategory: undefined,
    abstractCategory: undefined,
  },
  [EFieldType.NON_SEARCHABLE_TEXT]: {
    sumType: undefined,
    topLevelType: undefined,
    titleType: undefined,
    length: 50,
    max: 0,
    editable: true,
    listable: true,
    searchable: false,
    filter: '',
    //
    refSeparator: undefined,
    searchableInReservationList: false,
    reservationTextField: false,
    mandatory: false,
    unique: false,
    multiple: false,
    primary: false,
    sortable: false,
    virtualCategory: undefined,
    abstractCategory: undefined,
  },
  [EFieldType.NON_SEARCHABLE_REFERENCE]: {
    sumType: undefined,
    topLevelType: undefined,
    titleType: undefined,
    searchable: false,
    length: undefined,
    max: 0,
    listable: true,
    filter: '',
    //
    refSeparator: undefined,
    searchableInReservationList: false,
    reservationTextField: false,
    mandatory: false,
    unique: false,
    multiple: false,
    primary: false,
    sortable: false,
    virtualCategory: undefined,
    abstractCategory: undefined,
  },
};
